<template>
  <transition appear name="secondary-flyout-slide">
    <div
      v-show="open"
      :class="['owc-secondary-flyout__container', { 'owc-secondary-flyout__container--left': isLeftAligned }]"
    >
      <div class="owc-secondary-flyout__topbar-content">
        <!--TODO:ARIA :aria-label="backButtonLabel ? backButtonLabel : ''" fetched from inner store -->
        <button
          v-if="isInnerFlyout"
          class="owc-secondary-flyout__topbar-back-button wb-button"
          @click="handleFlyoutBackClick"
          tabindex="-1"
        >
          <div class="owc-secondary-flyout__topbar-back-button-wrapper">
            <wb-icon
              class="owc-secondary-flyout__topbar-icon wb-round-button__icon"
              aria-hidden="true"
              name="bds/chevron-left/24"
              flip-rtl
            />
          </div>
          {{ flyoutTitle }}
          <wb-counter
            v-if="flyoutTitleCounter"
            class="owc-secondary-flyout__topbar-title-counter"
            :value="flyoutTitleCounter"
            max="99"
            mode="primary"
          ></wb-counter>
        </button>
        <OwcLogo
          v-else
          class="owc-secondary-flyout__topbar-mobileElement owc-secondary-flyout__topbar-logo"
        />

        <!--TODO:ARIA -->
        <OwcHeaderButton
          class="owc-secondary-flyout__topbar-closeButton"
          aria-label="Close flyout"
          @click.native="handleFlyoutCloseClick"
          icon="bds/close/24"
          :light-mode="true"
        />
      </div>
      <SlotContainer class="owc-secondary-flyout__inner-content" refName="slot-content">
        <slot></slot>
      </SlotContainer>
    </div>
  </transition>
</template>

<script lang="ts">
import Vue from 'vue';
import OwcHeaderButton from '@owc-packages/Header/src/HeaderButton.vue';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import OwcLogo from '@owc-packages/Header/src/HeaderLogo.vue';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import {
  setupHostApi,
  dispatchCloseEvent,
  dispatchVisibilityEvent,
  FlyoutHost,
} from '@owc-utils/Seamless/flyoutIntegrations';
import headerMixin from '@owc-utils/Mixins/Header';
import { SecondaryFlyout, SecondaryFlyoutInstance } from './HeaderSecondaryFlyout';
import { handleFlyoutVisibilityOverride } from '../../_utils/Seamless/headerIntegrations';

export default (Vue as SecondaryFlyout).extend({
  name: 'owc-header-secondary-flyout',
  mixins: [headerMixin, OwcDataComponent],
  components: {
    OwcHeaderButton,
    OwcLogo,
    SlotContainer,
  },
  props: {
    open: {
      required: false,
      type: Boolean,
    },
    isInnerFlyout: {
      required: false,
      type: Boolean,
    },
    name: {
      required: true,
      type: String,
    },
    flyoutTitle: {
      required: false,
      type: String,
    },
    flyoutTitleCounter: {
      required: false,
      type: Number,
    },
    isLeftAligned: {
      required: false,
      type: Boolean,
    },
  },
  mounted() {
    setupHostApi(this, 'secondary-flyout-slide');

    if (this.open) {
      /**
       * Unfortunately we can't rely on simple events since that creates race conditions
       * between header created and event dispatched. So this override was created to ignore
       * the event and affect the store directly regardless of the header status.
       * This ensures that things like the region picker can initialize open even if it mounts
       * first than the header.
       *
       * The logic regarding the external integrations is already extremely complex, probably more than it
       * needs to be, but this will be refactored on the next PI (probably) when the header version INF is
       * released, which should have a leaner approach and not need most of this code.
       */
      handleFlyoutVisibilityOverride(this as SecondaryFlyoutInstance, this.$el as FlyoutHost, {
        isOpen: this.open,
        name: this.name,
        isInnerFlyout: this.isInnerFlyout,
      });
    }
  },
  data() {
    return {
      isInitialized: false,
    };
  },
  watch: {
    open(isOpen) {
      dispatchVisibilityEvent(this, { isOpen, name: this.name, isInnerFlyout: this.isInnerFlyout });
    },
  },
  methods: {
    handleFlyoutCloseClick() {
      dispatchCloseEvent(this);
    },
    handleFlyoutBackClick() {
      dispatchVisibilityEvent(this, {
        isOpen: false,
        name: this.name,
        isInnerFlyout: this.isInnerFlyout,
        isBackInteraction: true,
      });
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils';
@import '@owc-packages/Header/src/styles/flyout-vars';
@import './styles/flyout-common';

.owc-secondary-flyout {
  &__container {
    @include grid-columns-width(6);
    @include breakpoint-from(mq5) {
      @include grid-columns-width(4);
    }

    position: absolute;
    right: 0;

    z-index: $z-index-content-lvl9;

    margin-top: calc(9px + var(--wb-spacing-xxs));
    border-radius: 4px;

    background-color: var(--wb-white);

    box-shadow: var(--wb-shadow-level-3);

    @include breakpoint-to(mq2) {
      position: fixed;
      top: 0;
      left: 0;

      display: flex;
      flex-direction: column;

      width: 100%;
      max-width: none;
      height: 100vh;
      margin: 0;
      padding: 0;
      border-radius: 0;
    }
    @include breakpoint-between(mq1, mq5) {
      &--left {
        right: unset;
        left: 0;
      }
    }
  }
  &__topbar-content {
    @include wb-grid-gaps;

    display: grid;

    justify-items: stretch;

    padding: $flyout-spacing-small $flyout-spacing;

    background-color: var(--wb-black);

    @include breakpoint-from(mq3) {
      display: none;
    }
    @include breakpoint-to(mq2) {
      grid-template-areas: '. logo closeButton';
      grid-template-columns: $button-clickable-area auto $button-clickable-area;
      align-items: center;
    }
  }
  &__topbar-logo {
    grid-area: logo;
  }
  &__topbar-mobileElement {
    @include breakpoint-from(mq3) {
      display: none;
    }
  }
  &__topbar-closeButton {
    grid-area: closeButton;

    @include breakpoint-from(mq3) {
      color: var(--wb-grey-20);
    }
  }
  &__topbar-back-button-wrapper {
    @extend %button-normalize;
    @extend %flyout-button-color;

    display: flex;
    align-items: center;
    justify-content: center;

    width: $button-clickable-area;
    height: $button-clickable-area;
  }
  &__topbar-icon {
    width: $button-inner-icon-size--large;
    height: $button-inner-icon-size--large;
  }
  /*
   * This 'name' specificity shouldn't be necessary but.. workbench
  */
  &__topbar-icon[name='bds/chevron-left/24'] {
    @include breakpoint-to(mq2) {
      color: var(--wb-white);
    }
  }
  &__topbar-back-button {
    @include wb-type-copy-strong;

    @extend %button-normalize;
    @extend %flyout-button-color;

    display: flex;
    align-items: center;
    justify-self: flex-start;
  }
  &__topbar-title-counter {
    margin: var(--wb-spacing-xxs);
  }
  &__inner-content {
    //TODO: this needs to be better aligned with UX, this value comes from the invision page of the "worst case scenario" of the flyout
    @include breakpoint-from(mq3) {
      max-height: 582px;
    }
    overflow: auto;
  }
}
.secondary-flyout-slide {
  &-enter-to {
    transform: translateX(0);
    opacity: 1;

    @include breakpoint-from(mq3) {
      transform: translateY(0);
    }
  }
  &-leave-active,
  &-enter-active {
    transition: all $flyout-animation-time $flyout-animation-timing;
  }
  &-enter,
  &-leave-to {
    transform: translateX(100%);
    opacity: 0;

    @include breakpoint-to(mq2) {
      &.owc-secondary-flyout__container--left {
        transform: translateX(-100%);
      }
    }
    @include breakpoint-from(mq3) {
      transform: translateY($flyout-animation-translate);
    }
  }
}

@include rtl-rule('.owc-secondary-flyout__container') {
  right: unset;
  left: 0;
}
@include rtl-rule('.secondary-flyout-slide') {
  &-enter,
  &-leave-to {
    transform: translateX(-100%);
  }
}
</style>
